/**
 * Utility functions for SigTDD
 */

import { DEFAULTS } from './defaults';
import { DEFAULT_DISPLAY_TOGGLES } from '../types/plan';
import type { PhaseDirection } from '../types/plan';

/** Default phase direction by position: 1st=NBSB, 2nd=NB, 3rd=SB, 4th=NA, 5th+=NBSB */
const DEFAULT_PHASE_DIRECTIONS: PhaseDirection[] = ['NBSB', 'NB', 'SB', 'NA', 'NBSB', 'NBSB'];

/** Get the default direction for a phase by its position index (0-based) */
export function defaultPhaseDirection(index: number): PhaseDirection {
  return DEFAULT_PHASE_DIRECTIONS[index] ?? 'NBSB';
}

/** Generate a unique ID using crypto.randomUUID (browser native) */
export function generateId(): string {
  return crypto.randomUUID();
}

/** Create a default empty intersection */
export function createDefaultIntersection(sequenceNo: number): import('../types/plan').Intersection {
  const d = DEFAULTS.intersection;
  return {
    id: generateId(),
    sequenceNo,
    name: '',
    crossStreet: '',
    distanceToNext: d.distanceToNext.defaultValue,
    speedNorth: d.speedNorth.defaultValue,
    speedSouth: d.speedSouth.defaultValue,
    offset: d.offset.defaultValue,
    phases: ['A', 'B', 'C', 'D', 'E', 'F'].map((id, i) => ({
      id,
      duration: d.phaseDuration.defaultValue,
      direction: defaultPhaseDirection(i),
    })),
  };
}

/** Create an empty plan with default values */
export function createEmptyPlan(name = 'Untitled Plan'): import('../types/plan').Plan {
  const now = new Date().toISOString();
  return {
    id: generateId(),
    name,
    createdAt: now,
    updatedAt: now,
    intersections: [],
    settings: {
      cycleLength: DEFAULTS.plan.cycleLength.defaultValue,
      showGrid: true,
      showLabels: true,
      display: { ...DEFAULT_DISPLAY_TOGGLES },
    },
  };
}
